import React, { useState, useEffect, useReducer, createContext, Suspense } from "react";
import "./styles/global.scss";
import Navbar from "./components/Navbar";
import Login from "./components/Login";

import {useAuth, AuthProvider} from "./context/AuthProvider";
import RequireAuth from "./components/RequireAuth";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { AnimatePresence } from "framer-motion";

//Router
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";

//Socketio
import io from "socket.io-client";
import PersistLogin from "./components/PersistLogin";


import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';

const ServerDashboard = React.lazy(() => import("./components/ServerDashboard"));
const ProductDashboard = React.lazy(() => import("./components/ProductDashboard"));
const LuaDashboard = React.lazy(() => import("./components/LuaDashboard"));
const AuthDashboard = React.lazy(() => import("./components/AuthDashboard"));
const InviteDashboard = React.lazy(() => import("./components/InviteDashboard"));
const IPBanDashboard = React.lazy(() => import("./components/IPBanDashboard"));
const CreateInviteSection = React.lazy(() => import("./components/CreateInviteSection"));
const NewProductSection = React.lazy(() => import("./components/NewProductSection"));
const ManageProduct = React.lazy(() => import("./components/ManageProduct"));
const ManageLuaSubmission = React.lazy(() => import("./components/ManageLuaSubmission"));
const UserDashboard = React.lazy(() => import("./components/UserDashboard"));
const ManageUser = React.lazy(() => import("./components/ManageUser"));

const token = localStorage.getItem("token");
const isDev = process.env.REACT_APP_DEV === "true";

const socket = io.connect(isDev ? "http://localhost:28000" : "https://admin.anvil.team", {
	autoConnect: false,
	transports: ["websocket"],
	auth: { token },
});

//We immediately connect if a token was found (connection will get terminated if expired or invalid token)
if (token) {
	socket.connect();
}

function App() {

	//We have the log state here, so switching to server view doesnt reset it cause it gets unmounted
	const [logs, setLogs] = useReducer((logs, action) => {
		

		switch (action.type) {

			case "prepend": {
				console.log("prepending");
				return [
					...action.logs,
					...logs.slice(0, 30),
				];
			}

			case "append": {
				return [
					...logs.slice(0, 30),
					...action.logs
				]
			}

			case "overwrite": {
				return action.logs;
			}

			default: return logs.slice(0, 30);
		}
	}, []);

	const [consoleLines, setConsoleLines] = useReducer((lines, action) => {
		

		switch (action.type) {

			case "prepend": {
				return [
					...action.lines,
					...lines.slice(0, 200),
				];
			}

			case "append": {
				//console.log("appending", ...lines.slice(0, 10), ...action.lines);
				if (lines.length > 200)
					lines.shift();

				return [
					...lines,
					...action.lines
				]
			}

			case "overwrite": {
				return action.lines;
			}

			default: return lines.slice(0, 200);
		}
	}, []);

	const navigate = useNavigate();

	useEffect(() => {

		socket.removeAllListeners();

		socket.on("connect", () => console.log("Connected socket!"));

		socket.on("connect_error", error => {
			localStorage.removeItem("token");
			navigate("/login");
			toast.error("Server connection rejected");
			socket.disconnect();
			console.log(error);
		});

		socket.io.on("error", () => {
			console.log("error?");
		});

		socket.on("logentry", entry => {
			setLogs({type: "prepend", logs: [entry]});
		});

		socket.on("console-line", line => {
			setConsoleLines({type: "append", lines: [line]});
		});

		return () => {
			socket.removeAllListeners();
		}

	
	}, []);

	const location = useLocation();


	return (

			<AuthProvider>
				<Navbar />
				<Suspense 
					fallback={
						<Box 
							sx={{ 
								display: 'flex',
								height: "calc(100vh - 50px)",
								width: '100%',
								justifyContent: "center",
								alignItems: "center",
								flexDirection: "column",
								gap: "50px",
								color: "#fff",
								fontSize: "2rem",
								fontFamily: "Lato, sans-serif"
							}}
						>
							<LinearProgress className="lazy-loader-bar" sx={{ width: "100%", position: "absolute", top: "50px"}}/>
							<div>Fetching content ...</div>
						</Box>
					}
				>
					<AnimatePresence exitBeforeEnter>
						<Routes location={location} key={location.pathname}>
							<Route 
								path="/login" 
								element={
									<Login socket={socket} />
								} 
							/>
							<Route element={<PersistLogin />}>
								<Route 
									path="/server" 
									element={
										<RequireAuth>
											<ServerDashboard consoleLines={consoleLines} setConsoleLines={setConsoleLines}/>
										</RequireAuth>
									} 
								/>
								<Route 
									path="/add-dll" 
									element={
										<RequireAuth>
											<NewProductSection />
										</RequireAuth>
									} 
								/>
								<Route 
									path="/dlls" 
									element={
										<RequireAuth>
											<ProductDashboard />
										</RequireAuth>
									} 
								/>
								<Route 
									path="/luas" 
									element={
										<RequireAuth>
											<LuaDashboard />
										</RequireAuth>
									} 
								/>
								<Route 
									path="/lua-submission/:id" 
									element={
										<RequireAuth>
											<ManageLuaSubmission />
										</RequireAuth>
									} 
								/>
								<Route
									path="/dll/:id"
									element={
										<RequireAuth>
											<ManageProduct />
										</RequireAuth>
									}
								/>
								<Route 
									path="/invites" 
									element={
										<RequireAuth>
											<InviteDashboard />
										</RequireAuth>
									} 
								/>
								<Route 
									path="/ip" 
									element={
										<RequireAuth>
											<IPBanDashboard />
										</RequireAuth>
									} 
								/>
								<Route 
									path="/create-invite" 
									element={
										<RequireAuth>
											<CreateInviteSection />
										</RequireAuth>
									} 
								/>
								<Route 
									path="/users"
									element={
										<RequireAuth>
											<UserDashboard />
										</RequireAuth>
									}
								/>
								<Route
									path="/user/:id"
									element={
										<RequireAuth>
											<ManageUser />
										</RequireAuth>
									}
								/>
								<Route 
									path="/" 
									element={
										<RequireAuth>
											<AuthDashboard logs={logs} setLogs={setLogs} />
										</RequireAuth>
									} 
								/>
							</Route>
				
							<Route path="*" element={<p>Not found</p>} />
							
						</Routes>
					</AnimatePresence>
				</Suspense>
				<ToastContainer 
					position="bottom-right"
					draggable
					theme="dark"
					autoClose={2000}
				/>
			</AuthProvider>
	);
}

export default App;
